import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { Form } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import TextField from "@/common/elements/InputElements/TextField";
import DynamicForm from "@/common/DynamicForm";
import {
  cleanObject,
  getFormattedDayjsObjectToString,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  objectToArray,
  toQueryString,
  convertApiFields,
  getSQLTypeToHTMLType,
  hasCheckSecurity,
} from "@/util/common_function";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import JailIncidentInvolvedInmateInformation from "./JailIncidentInvolvedInmateInformation";
import JailIncidentInvolvedReportInformation from "./JailIncidentInvolvedReportInformation";
import JailIncidentClassViolationDetail from "./JailIncidentClassViolationDetail";
import JailIncidenSanctionInformation from "./JailIncidenSanctionInformation";
import JailIncidentInmateAdmitsGuilt from "./JailIncidentInmateAdmitsGuilt";
import JailIncidenHearingInformation from "./JailIncidenHearingInformation";
import JailIncidentIReviewHearings from "./JailIncidentIReviewHearings";
import Administrator from "@/components/JailIncidents/Administrator";
import Supervisor from "@/components/JailIncidents/Supervisor";
import { MODULES_LIST } from "@/util/common_variable";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import DataTable from "@/common/elements/DataTable";
import Modal from "@/common/elements/Modal";
import { useSelector } from "react-redux";
import notificationService from "@/util/notificationService";
import useDialog from "@/hooks/useDialog";

const JailIncidentsInvolvedDetail = forwardRef(
  (
    {
      isModify = false,
      data = null,
      editingIndex = null,
      reviewHearingData,
      setReviewHearingData,
      setApprovalSection,
      startingPoint,
    },
    ref,
  ) => {
    const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
    const { showDialog } = useDialog();
    const [involvementsForm] = Form.useForm();
    const [inmateSearchData, setInmateSearchData] = useState([]);
    const [inmateSearchAPILoading, setInmateSearchAPILoading] = useState(false);
    const [showInmateListModal, setShowInmateListModal] = useState(false);
    const [selectInmateDetails, setSelectInmateDetails] = useState(null);
    const [showBasicInfo, setShowBasicInfo] = useState(false);
    const [showTopBar, setShowTopBar] = useState(false);
    const [isFieldEnable, setisFieldEnable] = useState(bookingsFieldsEnabled);
    const [isInmateAvail, setisInmateAvail] = useState(false);
    const supervisorRef = useRef();
    const administratorRef = useRef();

    // Same rule as the main record/Supervisor/Administrator tabs: once
    // THIS inmate's own Administrator status is Approved, the whole record
    // locks for everyone (Admin included). Once only its Supervisor status
    // is Approved, it locks for everyone EXCEPT a user with Admin-View
    // permission, who can still edit.
    const hasAdminPermission = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.arbitary,
      LABELS_CAPITAL.VIEW,
      Arbitrary_Names.AdminView,
    );
    const inmateAdminApproved =
      data?.administratorStatus?.trim?.() === "Approved";
    const inmateSupervisorApproved =
      data?.supervisorStatus?.trim?.() === "Approved";
    const lockedByOwnApproval =
      inmateAdminApproved || (inmateSupervisorApproved && !hasAdminPermission);

    const hearingStartDateTime = Form.useWatch(
      "startDateTime",
      involvementsForm,
    );

    // -----------------------------------------------------------------------
    // JailIncidentsInvolvedInmateExtra — PCI's per-client dynamic "_U"
    // columns for this involved-inmate entry
    // (JailIncidentsInvolvedInmateInterface.asp's "Additional Inmate
    // Information" section). Shares the same flat `involvementsForm` as the
    // rest of this form, so the fields ride along in whatever
    // form.validateFields() returns — InvolvedInmates.jsx's handleSubmit
    // splits them back out using the field keys/type map/current PK exposed
    // via the ref below, the same way ChargesBailRecordsDetails.jsx does
    // for BookingBailsExtra.
    // -----------------------------------------------------------------------
    const [jailIncidentsInvolvedInmateExtraFields, setJailIncidentsInvolvedInmateExtraFields] = useState([]);
    const [jailIncidentsInvolvedInmateExtraFieldsReady, setJailIncidentsInvolvedInmateExtraFieldsReady] = useState(false);
    const jailIncidentsInvolvedInmateExtraTypeMapRef = useRef({});
    const jailIncidentsInvolvedInmateExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/JailIncidentsInvolvedInmateExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
          const converted = convertApiFields(tableData, "JailIncidentsInvolvedInmateExtra");
          const typeMap = {};
          converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
          jailIncidentsInvolvedInmateExtraTypeMapRef.current = typeMap;
          setJailIncidentsInvolvedInmateExtraFields(converted);
          setJailIncidentsInvolvedInmateExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error("[JailIncidentsInvolvedDetail] JailIncidentsInvolvedInmateExtra field defs fetch failed:", error);
        });
    }, []);

    // Fetches the saved extra row once a real jailIncidentsInvolvedInmateID
    // is present (edit mode) — a brand new entry has no id yet, so there's
    // nothing to fetch until it's saved once.
    useEffect(() => {
      if (!jailIncidentsInvolvedInmateExtraFieldsReady) return;
      const id = data?.jailIncidentsInvolvedInmateID;
      if (!isNonEmptyVariable(id)) {
        jailIncidentsInvolvedInmateExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("JailIncidentsInvolvedInmateExtra", "jailIncidentsInvolvedInmateID", id)
        .then((result) => {
          if (!result) return;
          jailIncidentsInvolvedInmateExtraIdRef.current = result.raw?.JailIncidentsInvolvedInmateExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = jailIncidentsInvolvedInmateExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          involvementsForm.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error("[JailIncidentsInvolvedDetail] JailIncidentsInvolvedInmateExtra fetch failed:", error);
        });
    }, [data?.jailIncidentsInvolvedInmateID, jailIncidentsInvolvedInmateExtraFieldsReady]);

    useImperativeHandle(ref, () => ({
      form: involvementsForm,
      selectInmateDetails: selectInmateDetails,
      // Reads the Supervisor/Administrator sections' current selections
      // directly off their forms - see Supervisor.jsx/Administrator.jsx -
      // rather than trusting the debounced updateForm sync to have already
      // landed by the time Save is clicked.
      getApprovalValues: () => ({
        ...(supervisorRef.current?.getValues() || {}),
        ...(administratorRef.current?.getValues() || {}),
      }),
      // JailIncidentsInvolvedInmateExtra field keys/type map/current PK —
      // read by InvolvedInmates.jsx's handleSubmit to split the extra
      // fields out of the validated form values.
      jailIncidentsInvolvedInmateExtraFieldKeys: jailIncidentsInvolvedInmateExtraFields.map((f) => f.columnName),
      jailIncidentsInvolvedInmateExtraTypeMap: jailIncidentsInvolvedInmateExtraTypeMapRef.current,
      jailIncidentsInvolvedInmateExtraID: jailIncidentsInvolvedInmateExtraIdRef.current,
    }));

    useEffect(() => {
      if (isModify && !lockedByOwnApproval) {
        if (isNonEmptyVariable(editingIndex)) {
          setShowBasicInfo(false);
          setShowTopBar(true);
        } else {
          setShowBasicInfo(true);
          setShowTopBar(false);
        }

        setisFieldEnable(true);
      } else {
        setShowBasicInfo(false);
        setShowTopBar(true);
        setisFieldEnable(false);
      }
    }, [isModify, lockedByOwnApproval, editingIndex]);

    useEffect(() => {
      if (isNonEmptyVariable(hearingStartDateTime)) {
        if (
          !isNonEmptyVariable(
            involvementsForm.getFieldValue("hearingDueDateTime"),
          )
        ) {
          involvementsForm.setFieldValue(
            "hearingDueDateTime",
            dayjs(hearingStartDateTime).add(5, "day"),
          );
        }
      }
    }, [hearingStartDateTime]);

    const tableHeaders = [
      { key: "name", text: "Name" },
      { key: "bookingNumber", text: "Booking Number" },
      { key: "arrestDate", text: "Arrested Date" },
    ];

    const disciplineRequired = Form.useWatch(
      "disciplineRequired",
      involvementsForm,
    );

    const majorOptionsAll = getOptionsFromRefList(
      "JailIncidentsMajorViolations",
      false,
      false,
      true,
    );

    const minorOptionsAll = getOptionsFromRefList(
      "JailIncidentsMinorViolations",
      false,
      false,
      true,
    );

    const classIOptionsAll = getOptionsFromRefList(
      "JailIncidentsclassIViolations",
      false,
      false,
      true,
    );

    const sanctionOptionsAll = getOptionsFromRefList(
      "JailIncidentsSanctions",
      false,
      false,
      true,
    );

    const combineDateTime = (date, time) => {
      return date && time ? dayjs(`${date} ${time}`, "MM/DD/YYYY HH:mm") : null;
    };

    useEffect(() => {
      if (isNonEmptyVariable(data)) {
        involvementsForm.setFieldsValue({
          ...data,
          jailIncidentsInvolvedInmateReviewHearings:
            data?.jailIncidentsInvolvedInmateReviewHearings || [],
          disciplineRequired: isNonEmptyVariable(data?.disciplineRequired)
            ? data?.disciplineRequired?.trim()
            : null,
          supervisorName: getStaffName(data?.supervisorStaffID),
          administratorName: getStaffName(data?.administratorStaffID),
          supervisorDateTime: combineDateTime(
            data?.supervisorDate,
            data?.supervisorTime,
          ),
          hearingDateTime: combineDateTime(
            data?.hearingDate,
            data?.hearingTime,
          ),
          startDateTime: combineDateTime(
            data?.hearingStartDate,
            data?.hearingStartTime,
          ),
          hearingDueDateTime: combineDateTime(
            data?.hearingDueDate,
            data?.hearingDueTime,
          ),
          administratorDateTime: combineDateTime(
            data?.administratorDate,
            data?.administratorTime,
          ),
          penaltyStartDateTime: combineDateTime(
            data?.penaltyStartDate,
            data?.penaltyStartTime,
          ),
          penaltyEndDateTime: combineDateTime(
            data?.penaltyEndDate,
            data?.penaltyEndTime,
          ),
          acceptDateTime: combineDateTime(data?.acceptDate, data.acceptTime),
        });

        if (isNonEmptyVariable(data?.bookingID)) {
          onSearchInmate({
            bookingId: data?.bookingID,
          });
          setisInmateAvail(true);
        }
      }
    }, [data]);

    useEffect(() => {
      if (inmateSearchData?.length) {
        if (!isInmateAvail) {
          setShowInmateListModal(true);
        } else {
          handleInmateListRowClick(inmateSearchData?.[0]);
        }
      }
    }, [inmateSearchData]);

    const getStaffName = (staffID) => {
      return getRefListNameByID("Staff", staffID, true) || "N/A";
    };

    const onSearchInmate = (value) => {
      console.log("Value : ", value);
      const newValue = {
        ...value,
        searchType: "Starts With",
        isBookingList: true,
      };
      const prePayload = cleanObject(newValue);
      let filter = [];
      if (isNonEmptyVariable(prePayload)) {
        filter = objectToArray(prePayload, "filterKey", "value");
      }

      getFolderSearchData(filter, value);
    };

    const getFolderSearchData = (filter, value) => {
      let url;
      const isBookingNumber = value?.isBookingNumber;

      if (isBookingNumber) {
        const bookingNumber = value?.bookingNumber;
        url = `${API_ENDPOINTS.inmateSearchByBookingNumber}${bookingNumber}`;
      } else {
        url = API_ENDPOINTS.facilityLog.getInmate;

        const obj = {
          page: 0,
          size: 500,
        };

        const urlParam = toQueryString(obj);
        if (isNonEmptyVariable(urlParam)) {
          url += "?" + urlParam;
        }
      }

      setInmateSearchAPILoading(true);

      const request = isBookingNumber
        ? api.get(url)
        : api.post(url, filter);

      request
        .then((response) => {
          let data = response?.data;

          // ✅ Safe normalize
          const normalizedData = data
            ? Array.isArray(data)
              ? data
              : [data]
            : [];

          if (isBookingNumber) {
            if (!normalizedData.length) {
              notificationService.error("No booking found");
              return;
            }

            if (normalizedData[0]?.inJail === false) {
              showDialog("info", {
                title: "Booking Found",
                content: "Booking found, Inmate not in jail",
                confirmButtonText: "Ok",
              });
              return;
            }
          }

          const rowsData = normalizedData
            .map((jsd) => ({
              ...jsd,
              name: `${jsd?.lname || jsd?.lastname}, ${jsd?.fname || jsd?.firstName}${
                isNonEmptyVariable(jsd?.mname || jsd?.middleName)
                  ? ", " + (jsd?.mname || jsd?.middleName)
                  : ""
              }`,
            }));

          setInmateSearchData(rowsData);
        })
        .catch((error) => {
          console.error("API error:", error);
          if (isBookingNumber) {
            notificationService.error("No booking found");
          }
        })
        .finally(() => {
          setInmateSearchAPILoading(false);
        });
    };

    const handleInmateListRowClick = (data) => {
      if (isNonEmptyVariable(data)) {
        setSelectInmateDetails(data);
        setShowInmateListModal(false);
        setShowBasicInfo(() => false);
        setShowTopBar(() => true);
        setisInmateAvail(false);
      }
    };

    const updateForm = (data) => {
      setApprovalSection((prev) => ({
        ...(prev || {}),
        ...data,
      }));
    };

    return (
      <>
        {/* Inmate Information and Report Information are always shown */}
        <JailIncidentInvolvedInmateInformation
          selectedInmate={selectInmateDetails}
          setSelectInmateDetails={setSelectInmateDetails}
          showBasicInfo={showBasicInfo}
          setShowBasicInfo={setShowBasicInfo}
          from={MODULES_LIST.JAIL_INCIDENTS}
          onSave={onSearchInmate}
          showTopBar={showTopBar}
          setShowTopBar={setShowTopBar}
          inmateSearchAPILoading={inmateSearchAPILoading}
          isFieldEnable={isFieldEnable}
          startingPoint={startingPoint}
        />
        <Form form={involvementsForm}>
          <JailIncidentInvolvedReportInformation
            data={involvementsForm}
            disciplineRequired={disciplineRequired}
            isFieldEnable={isFieldEnable}
            startingPoint={startingPoint}
          />

          {isNonEmptyVariable(disciplineRequired) &&
            (disciplineRequired === "Discipline Required" ||
              disciplineRequired === "Verbal Warning") && (
              <>
                <JailIncidentClassViolationDetail
                  involvementsForm={involvementsForm}
                  majorOptionsAll={majorOptionsAll}
                  minorOptionsAll={minorOptionsAll}
                  classIOptionsAll={classIOptionsAll}
                  isFieldEnable={isFieldEnable}
                />
                {disciplineRequired === "Discipline Required" && (
                  <JailIncidenSanctionInformation
                    involvementsForm={involvementsForm}
                    sanctionOptionsAll={sanctionOptionsAll}
                    isFieldEnable={isFieldEnable}
                  />
                )}
                <JailIncidentInmateAdmitsGuilt
                  data={data}
                  isFieldEnable={isFieldEnable}
                  startingPoint={startingPoint}
                />
                <JailIncidenHearingInformation
                  data={involvementsForm}
                  isFieldEnable={isFieldEnable}
                  startingPoint={startingPoint}
                />
              </>
            )}

          {/* Review Hearings Section is always shown */}
          <JailIncidentIReviewHearings
            data={reviewHearingData}
            setReviewHearingData={setReviewHearingData}
            isFieldEnable={isFieldEnable}
            startingPoint={startingPoint}
          />

          {/* JailIncidentsInvolvedInmateExtra — always shown, same as
              Review Hearings above (PCI places this unconditionally too,
              not gated by disciplineRequired like the violation/sanction/
              hearing sections below). */}
          {jailIncidentsInvolvedInmateExtraFields.length > 0 && (
            <>
              <StyledSectionHeading marginTop="20px">
                <span>Additional Inmate Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={16}>
                {jailIncidentsInvolvedInmateExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={involvementsForm}
                    bookingsFieldsEnabled={isFieldEnable}
                  />
                ))}
              </StyledGridRow>
            </>
          )}

          {(disciplineRequired === "Discipline Required" ||
            disciplineRequired === "Verbal Warning") && (
            <>
              {/* Approval Section */}
              <StyledSectionHeading marginTop="20px">
                <span>Approval Information</span>
              </StyledSectionHeading>
              <StyledGridRow>
                <StyledGridCol marginBottom="15px">
                  <FormItem
                    label="Approved?"
                    layout="horizontal"
                    labelPostion="label-right"
                    name="approved"
                    valuePropName="checked"
                  >
                    <Checkbox disabled={!isFieldEnable} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
              <Supervisor
                ref={supervisorRef}
                data={data}
                parentFieldEnable={isModify}
                from={MODULES_LIST.JAIL_INCIDENTS}
                involvementsForm={involvementsForm}
                updateForm={updateForm}
                startingPoint={startingPoint}
              />
              <Administrator
                ref={administratorRef}
                data={data}
                parentFieldEnable={isModify}
                from={MODULES_LIST.JAIL_INCIDENTS}
                involvementsForm={involvementsForm}
                updateForm={updateForm}
                startingPoint={startingPoint}
              />
            </>
          )}
        </Form>

        <Modal
          width={"50%"}
          header="Select One"
          show={showInmateListModal}
          handleCancel={() => setShowInmateListModal(false)}
          onHide={() => setShowInmateListModal(false)}
          footerActionsNeed={false}
        >
          <DataTable
            rowUniqueId={"bookingNumber"}
            headers={tableHeaders}
            width="100%"
            className="table-striped"
            selectionColumnVisible={false}
            data={inmateSearchData || []}
            onRowClick={handleInmateListRowClick}
          />
        </Modal>
      </>
    );
  },
);

export default JailIncidentsInvolvedDetail;
